import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import ModalPortal from "../common/ModalPortal";

// Actor names are enriched server-side (Activity v2's ActorNameService).
// The `*ByName` fields on each row are already resolved; this is only the
// fallback for a resolver miss or an older payload shape.
const actorLabel = (byName, actor) => {
  if (byName && byName.trim()) return byName.trim();
  if (!actor) return "";
  if (typeof actor === "string") return actor.trim();
  if (actor.who === "service") return "System";
  const label = actor.who ? actor.who[0].toUpperCase() + actor.who.slice(1) : "";
  if (label && actor.whoId) return `${label} · ${actor.whoId}`;
  return label || "";
};

/**
 * "Deleted entries" modal for Physical activity — reuses the shared
 * .fh3-del-* SCSS shell (family history / allergies) with columns
 * chosen to fit the activity domain: Frequency (days per week +
 * minutes per session), Intensity, Description, plus the Added / Deleted
 * audit lines.
 */
const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  return `${date}, ${time}`;
};

const frequencyText = (a) => {
  const days = a && a.daysPerWeek;
  const mins = a && a.minutesPerSession;
  if (!days && !mins) return "—";
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"} a week`);
  if (mins) parts.push(`${mins} min${mins === 1 ? "" : "s"} per session`);
  return parts.join(" · ");
};

const capitalise = (s) =>
  typeof s === "string" && s.length > 0 ? s[0].toUpperCase() + s.slice(1) : "—";

const DeletedEntriesModal = ({ patient, entries, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Most-recently-deleted first — matches the shared DeletedEntriesDialog
  // used by Allergies + Family history so all three sections agree.
  const sortedEntries = useMemo(() => {
    const list = entries || [];
    const ts = (d) => {
      if (!d) return -Infinity;
      const t = new Date(d).getTime();
      return isNaN(t) ? -Infinity : t;
    };
    return [...list].sort((a, b) => ts(b.deletedAt) - ts(a.deletedAt));
  }, [entries]);

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal fh3-del-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pa-del-title"
        >
          <header className="fh3-del-head">
            <h2 id="pa-del-title" className="fh3-del-title">
              Deleted entries
              <span className="fh3-del-sub"> · Physical activity</span>
            </h2>
            <button
              type="button"
              className="fh3-del-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </header>
  
          <div className="fh3-del-body">
            {sortedEntries.length === 0 ? (
              <p className="fh3-del-empty">No deleted entries yet.</p>
            ) : (
              sortedEntries.map((a) => (
                <article className="fh3-del-card" key={a.activityId}>
                  <header className="fh3-del-card-head">
                    <h3 className="fh3-del-card-title">{a.name}</h3>
                    <span className="fh3-del-pill">Deleted</span>
                  </header>
  
                  <dl className="fh3-del-dl">
                    <dt>Frequency</dt>
                    <dd>{frequencyText(a)}</dd>
                    <dt>Intensity</dt>
                    <dd>{capitalise(a.intensity)}</dd>
                    <dt>Description</dt>
                    <dd>{(a.description && a.description.trim()) || "—"}</dd>
                  </dl>
  
                  <footer className="fh3-del-audit">
                    <div className="fh3-del-audit-cell">
                      <span className="fh3-del-audit-label">ADDED</span>
                      <span className="fh3-del-audit-value">
                        {actorLabel(a.addedByName, a.addedBy) || "—"}
                        {a.addedAt ? ` · ${formatDateTime(a.addedAt)}` : ""}
                      </span>
                    </div>
                    <div className="fh3-del-audit-cell is-danger">
                      <span className="fh3-del-audit-label">DELETED</span>
                      <span className="fh3-del-audit-value">
                        {actorLabel(a.deletedByName, a.deletedBy) || "—"}
                        {a.deletedAt ? ` · ${formatDateTime(a.deletedAt)}` : ""}
                      </span>
                    </div>
                  </footer>
                </article>
              ))
            )}
          </div>
  
          <footer className="fh3-del-foot">
            <button
              type="button"
              className="sec-btn sec-btn-ghost fh3-del-close-btn"
              onClick={onClose}
            >
              Close
            </button>
          </footer>
        </div>
      </div>
    </ModalPortal>
  );
};

DeletedEntriesModal.propTypes = {
  patient: PropTypes.object,
  entries: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

export default DeletedEntriesModal;
